"use client";

import { createPortal } from "react-dom";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AppWindow,
  ArrowLeft,
  Boxes,
  Cable,
  ChevronRight,
  Clapperboard,
  Database,
  Gauge,
  HardDrive,
  Home,
  LayoutGrid,
  MoreHorizontal,
  MoreVertical,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Workflow,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useRunyiValueSync, publishRunyiValueChange } from "../lib/runyi-value-sync";
import { StatusIndicator } from "@/components/runyi/status-indicator";
import { EnterpriseContextMenu } from "./enterprise-context-menu";
import { enterpriseWorkspace, enterpriseWorkspaces, primaryEnterpriseWorkspaces, allEnterpriseRoutes, type EnterpriseRoute } from "./enterprise-workspace-registry";
import { UniversalInfrastructureCreationPlane } from "./universal-infrastructure-creation-plane";
import { useRunyiUiControlPlane } from "./runyi-ui-coordinator";

export type { EnterpriseRoute } from "./enterprise-workspace-registry";

type HealthLike = {
  configured: boolean;
  connected: boolean;
  signedIn?: boolean;
  movieCount?: number;
  role?: "OWNER_ADMIN" | "ADMIN" | "MEMBER";
  activeUserCount?: number;
  maximumUsers?: number;
};

type NavItem = {
  id: EnterpriseRoute;
  label: string;
  mobileLabel: string;
  icon: LucideIcon;
  description?: string;
  keywords?: string[];
};

type SearchItem = NavItem & { group: string };
type UniversalSearchHit = {
  key: string;
  title: string;
  subtitle: string;
  route: EnterpriseRoute;
  kind: "ENTITY" | "SETUP" | "ACCOUNT";
};

const allRoutes: EnterpriseRoute[] = [...allEnterpriseRoutes];

export const primaryNavigation: NavItem[] = primaryEnterpriseWorkspaces.map((workspace) => ({
  id: workspace.route, label: workspace.title, mobileLabel: workspace.mobileLabel ?? workspace.title,
  icon: workspace.icon ?? LayoutGrid, description: workspace.description,
}));

const controlPlaneIconMap: Record<string, LucideIcon> = {
  database: Database,
  plus: Plus,
  "app-window": AppWindow,
  "hard-drive": HardDrive,
  settings: Settings2,
  "settings-2": Settings2,
  layout: LayoutGrid,
};

function resolveControlPlanePrimaryNavigation(controlPlane: { navigation_entries?: Array<Record<string, any>> } | null): NavItem[] {
  const resolved: NavItem[] = [];

  for (const entry of controlPlane?.navigation_entries ?? []) {
    const route = String(entry.metadata?.route ?? "") as EnterpriseRoute;
    if (!allRoutes.includes(route)) continue;
    resolved.push({
      id: route,
      label: String(entry.display_name ?? route),
      mobileLabel: String(entry.metadata?.mobile_label ?? entry.display_name ?? route),
      icon: controlPlaneIconMap[String(entry.icon_key ?? "").toLowerCase()] ?? LayoutGrid,
      description: entry.description ? String(entry.description) : undefined,
    });
  }

  return resolved.length > 0 ? resolved : primaryNavigation;
}

export const functionalGroups: Array<{ title: string; description: string; items: NavItem[] }> = [
  {
    title: "Infrastructure",
    description: "Availability, inventory, usage, capacity, provider truth and health.",
    items: [
      { id: "infrastructure", label: "Infrastructure Overview", mobileLabel: "Overview", icon: Database, description: "Inventory, health, usage, capacity, application consumption and provider truth.", keywords: ["inventory","usage","capacity","health","cost","resource"] },
      { id: "catalogue", label: "Available Infrastructure", mobileLabel: "Available", icon: LayoutGrid, description: "Provisionable and connectable provider-neutral capabilities.", keywords: ["available","catalogue","database","storage","compute","service"] },
      { id: "catalogue", label: "Providers", mobileLabel: "Providers", icon: Boxes, description: "Provider capabilities, readiness, regions and compatibility.", keywords: ["supabase","cloudflare","firebase","provider","region"] },
      { id: "infrastructure", label: "Usage & Capacity", mobileLabel: "Capacity", icon: Gauge, description: "Verified utilization, limits, headroom, source and last verification.", keywords: ["usage","capacity","utilization","quota","headroom"] },
    ],
  },
  {
    title: "Create",
    description: "Acquire marketplace infrastructure or create applications through governed workflows.",
    items: [
      { id: "new", label: "Universal Create", mobileLabel: "Create", icon: Plus, description: "Acquire, create or compose marketplace infrastructure, applications, blueprints and future resource classes.", keywords: ["factory","new","create","blueprint","advanced","uicp"] },
      { id: "bridge", label: "Add Existing Infrastructure", mobileLabel: "Add Existing", icon: Cable, description: "Connect, verify and register infrastructure that already exists.", keywords: ["existing","import","connect","register","bridge"] },
    ],
  },
  {
    title: "Applications",
    description: "Application-scoped infrastructure and usage.",
    items: [
      { id: "applications", label: "Applications", mobileLabel: "Apps", icon: AppWindow, description: "Applications, environments, assigned infrastructure, usage and dashboards.", keywords: ["application","environment","resource","usage","dashboard"] },
      { id: "movies", label: "Movies Engine", mobileLabel: "Movies", icon: Clapperboard, description: "Open the application-scoped Movie Creative Engine.", keywords: ["movie","creative","film"] },
    ],
  },
  {
    title: "Operations",
    description: "Infrastructure lifecycle execution and status.",
    items: [
      { id: "operations", label: "Operations", mobileLabel: "Ops", icon: Workflow, description: "Provisioning, deployments, reconciliation, scaling, migrations, incidents and jobs.", keywords: ["provision","deploy","reconcile","scale","migration","incident","job"] },
      { id: "flow", label: "Flow", mobileLabel: "Flow", icon: Workflow, description: "Governed execution, verification, release and rollback.", keywords: ["deployment","automation","release","rollback"] },
      { id: "bridge", label: "Connections / Bridge", mobileLabel: "Bridge", icon: Cable, description: "Provider accounts, APIs, OAuth, bindings and assignments.", keywords: ["connection","binding","api","oauth","provider"] },
    ],
  },
  {
    title: "AI",
    description: "System-wide assistant.",
    items: [
      { id: "ai", label: "RUNYI AI Assistant", mobileLabel: "AI", icon: Sparkles, description: "Open the permission-aware AI workspace from any RUNYI screen.", keywords: ["ai","assistant","chat","work","architect"] },
    ],
  },
  {
    title: "Data & Files",
    description: "Universal workspaces, authorized files and provider-backed objects.",
    items: [
      { id: "files", label: "Universal Workspace Explorer", mobileLabel: "Files", icon: HardDrive, description: "Browse, search, inspect, upload and securely download authorized workspace objects.", keywords: ["files","file manager","workspace","storage","objects","documents","uploads","download","explorer","ufe","uwe"] },
    ],
  },
  {
    title: "Governance & Tools",
    description: "Authority, accounts, settings and secondary tools.",
    items: [
      { id: "governance", label: "Accounts & Governance", mobileLabel: "Governance", icon: ShieldCheck, description: "Accounts, admins, roles, permissions, approvals and audit.", keywords: ["account","admin","role","permission","m4","approval","audit"] },
      { id: "governance", label: "Settings", mobileLabel: "Settings", icon: Settings2, description: "Account, appearance and system settings.", keywords: ["settings","theme","support"] },
      { id: "more", label: "All Functions", mobileLabel: "More", icon: LayoutGrid, description: "Search the full RUNYI function directory.", keywords: ["all","functions","tools","menu"] },
    ],
  },
];

const infrastructureCategories: SearchItem[] = [
  { id: "infrastructure", label: "Databases", mobileLabel: "Databases", icon: Database, group: "Infrastructure Categories", description: "SQL, NoSQL, graph and time-series data services.", keywords: ["postgres","sql","nosql","graph","database"] },
  { id: "infrastructure", label: "Vector & Search", mobileLabel: "Vector", icon: Search, group: "Infrastructure Categories", description: "Vector databases, embeddings, search engines and indexes.", keywords: ["vector","embedding","search","index"] },
  { id: "infrastructure", label: "Storage", mobileLabel: "Storage", icon: HardDrive, group: "Infrastructure Categories", description: "Object, file, block, archive, media and backup storage.", keywords: ["object","file","block","archive","backup"] },
  { id: "infrastructure", label: "Compute & Edge", mobileLabel: "Compute", icon: Activity, group: "Infrastructure Categories", description: "VMs, containers, serverless, edge and GPU compute.", keywords: ["vm","container","serverless","edge","gpu"] },
  { id: "infrastructure", label: "Backend & APIs", mobileLabel: "Backend", icon: Boxes, group: "Infrastructure Categories", description: "Backend services, APIs, gateways, queues and webhooks.", keywords: ["backend","api","gateway","queue","webhook"] },
  { id: "infrastructure", label: "Network", mobileLabel: "Network", icon: Cable, group: "Infrastructure Categories", description: "DNS, CDN, WAF, VPN, tunnels and load balancing.", keywords: ["dns","cdn","waf","vpn","tunnel"] },
  { id: "infrastructure", label: "Identity & Security", mobileLabel: "Security", icon: ShieldCheck, group: "Infrastructure Categories", description: "Auth, IAM, OAuth, SSO, RLS, secrets and certificates.", keywords: ["auth","iam","oauth","sso","rls","secret"] },
  { id: "infrastructure", label: "AI & Models", mobileLabel: "AI", icon: Sparkles, group: "Infrastructure Categories", description: "Models, inference, embeddings, agents and AI gateways.", keywords: ["model","inference","agent","mcp"] },
];

const routeTitles: Record<EnterpriseRoute,string> = Object.fromEntries(
  enterpriseWorkspaces.map((workspace) => [workspace.route, workspace.title]),
) as Record<EnterpriseRoute,string>;

function searchItems(query: string): SearchItem[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const items: SearchItem[] = [
    ...functionalGroups.flatMap((group) => group.items.map((item) => ({ ...item, group: group.title }))),
    ...infrastructureCategories,
  ];
  return items.filter((item) => [item.label,item.description,item.group,...(item.keywords ?? [])].filter(Boolean).join(" ").toLowerCase().includes(needle));
}

function groupSearchResults(items: SearchItem[]) {
  return items.reduce<Record<string,SearchItem[]>>((groups,item) => { (groups[item.group] ??= []).push(item); return groups; },{});
}

function primaryRouteFor(route: EnterpriseRoute): EnterpriseRoute | null {
  return enterpriseWorkspace(route).primaryRoute;
}

function universalEntityRoute(entityType: string, domain: string): EnterpriseRoute {
  const type = entityType.toUpperCase();
  const area = domain.toUpperCase();
  if (type === "APPLICATION" || type.includes("INFRASTRUCTURE_PROJECT") || area.includes("APPLICATION")) return "applications";
  if (type.includes("WORKSPACE") || type.includes("FILE") || area.includes("WORKSPACE") || area.includes("FILE")) return "files";
  if (area.includes("SETTING") || area.includes("GOVERNANCE")) return "governance";
  return "infrastructure";
}

export function EnterpriseHeader({ activeRoute, health, loading, onNavigate, onRefresh, supabase }: {
  activeRoute: EnterpriseRoute;
  health: HealthLike;
  loading: boolean;
  onNavigate: (route: EnterpriseRoute) => void;
  onRefresh: () => void;
  supabase: SupabaseClient | null;
}) {
  const headerRef = useRef<HTMLElement | null>(null);
  const previousRouteRef = useRef<EnterpriseRoute | null>(null);
  const routeStackRef = useRef<EnterpriseRoute[]>([]);
  const stackReadyRef = useRef(false);
  const backTransitionRef = useRef(false);
  const restoreScrollRouteRef = useRef<EnterpriseRoute | null>(null);
  const [searchOpen,setSearchOpen] = useState(false);
  const [menuOpen,setMenuOpen] = useState(false);
  const [query,setQuery] = useState("");
  const [universalHits,setUniversalHits] = useState<UniversalSearchHit[]>([]);
  const [canGoBack,setCanGoBack] = useState(false);
  const [viewportHeight,setViewportHeight] = useState<number | null>(null);
  const [creationPlaneOpen,setCreationPlaneOpen] = useState(true);
  const globalValueSync = useRunyiValueSync({ supabase, connected: Boolean(health.connected), domains: ["all"], onRefresh, intervalMs: 60_000 });
  const syncTitle = globalValueSync.lastSyncedAt
    ? `OWNER ROOT values: ${globalValueSync.state} · Last confirmed ${new Date(globalValueSync.lastSyncedAt).toLocaleTimeString()}`
    : `OWNER ROOT values: ${globalValueSync.state} · Awaiting authoritative response`;
  const refreshUniversalValues = () => {
    publishRunyiValueChange(["all"], "manual");
    onRefresh();
  };

  const globalResults = useMemo(() => searchItems(query),[query]);
  const groupedResults = useMemo(() => groupSearchResults(globalResults),[globalResults]);

  useEffect(() => {
    if (!searchOpen || !query.trim() || !supabase) {
      setUniversalHits([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const pattern = `%${query.trim()}%`;
      const entityFields = "entity_key,entity_type_key,display_name,domain,tenant_project_ref,environment_key,lifecycle_state,health_state,identity_email,identity_email_type,identity_email_verified,sync_state,last_synced_at";
      const [entityName,entityKey,entityEmail,setups,accounts] = await Promise.all([
        supabase.from("runyi_universal_entity_registry").select(entityFields).ilike("display_name",pattern).limit(12),
        supabase.from("runyi_universal_entity_registry").select(entityFields).ilike("entity_key",pattern).limit(12),
        supabase.from("runyi_universal_entity_registry").select(entityFields).ilike("identity_email",pattern).limit(12),
        supabase.from("runyi_new_infrastructure_setups").select("setup_key,display_name,provider_key,target_member_email,target_account_ref,target_project_ref,lifecycle_state,backend_stage").ilike("target_member_email",pattern).limit(8),
        supabase.from("engine_allowed_users").select("email,role,active,user_id").ilike("email",pattern).limit(8),
      ]);
      if (cancelled) return;
      const hits = new Map<string,UniversalSearchHit>();
      for (const row of [...(entityName.data ?? []),...(entityKey.data ?? []),...(entityEmail.data ?? [])] as Array<Record<string,unknown>>) {
        const key = String(row.entity_key ?? "");
        if (!key || hits.has(key)) continue;
        hits.set(key,{
          key,
          title:String(row.display_name ?? key),
          subtitle:[row.identity_email || "IDENTITY PENDING",row.entity_type_key,row.domain,row.sync_state,row.health_state].filter(Boolean).join(" · "),
          route:universalEntityRoute(String(row.entity_type_key ?? ""),String(row.domain ?? "")),
          kind:"ENTITY",
        });
      }
      for (const row of (setups.data ?? []) as Array<Record<string,unknown>>) {
        const key = `SETUP:${String(row.setup_key ?? row.target_project_ref ?? row.target_member_email ?? "")}`;
        if (!key || hits.has(key)) continue;
        hits.set(key,{
          key,
          title:String(row.display_name ?? row.target_project_ref ?? row.provider_key ?? "Infrastructure setup"),
          subtitle:[row.provider_key,row.target_member_email || row.target_account_ref,row.target_project_ref,row.backend_stage].filter(Boolean).join(" · "),
          route:"new",
          kind:"SETUP",
        });
      }
      for (const row of (accounts.data ?? []) as Array<Record<string,unknown>>) {
        const key = `ACCOUNT:${String(row.user_id ?? row.email ?? "")}`;
        if (!key || hits.has(key)) continue;
        hits.set(key,{
          key,
          title:String(row.email ?? "RUNYI account"),
          subtitle:[row.role,row.active === false ? "INACTIVE" : "ACTIVE"].filter(Boolean).join(" · "),
          route:"files",
          kind:"ACCOUNT",
        });
      }
      setUniversalHits(Array.from(hits.values()).slice(0,30));
    },120);
    return () => { cancelled = true; window.clearTimeout(timer); };
  },[query,searchOpen,supabase]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setViewportHeight(window.visualViewport?.height ?? window.innerHeight);
    update();
    window.visualViewport?.addEventListener("resize",update);
    window.addEventListener("resize",update);
    return () => { window.visualViewport?.removeEventListener("resize",update); window.removeEventListener("resize",update); };
  },[]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!stackReadyRef.current) {
      try {
        const stored=JSON.parse(window.sessionStorage.getItem("runyi:route-stack") ?? "[]");
        routeStackRef.current=Array.isArray(stored)
          ? stored.filter((route): route is EnterpriseRoute => typeof route==="string" && allRoutes.includes(route as EnterpriseRoute)).slice(-50)
          : [];
      } catch {
        routeStackRef.current=[];
      }
      stackReadyRef.current=true;
      previousRouteRef.current=activeRoute;
      setCanGoBack(routeStackRef.current.length>0);
      window.history.replaceState({...(window.history.state ?? {}),runyiRoute:activeRoute},"",window.location.href);
      return;
    }

    const wasBackTransition=backTransitionRef.current;
    if (wasBackTransition) {
      backTransitionRef.current=false;
    } else if (previousRouteRef.current && previousRouteRef.current!==activeRoute) {
      const stack=routeStackRef.current;
      if (stack[stack.length-1]!==previousRouteRef.current) stack.push(previousRouteRef.current);
      routeStackRef.current=stack.slice(-50);
    }

    previousRouteRef.current=activeRoute;
    try { window.sessionStorage.setItem("runyi:route-stack",JSON.stringify(routeStackRef.current)); } catch {}
    setCanGoBack(routeStackRef.current.length>0);
    const state={...(window.history.state ?? {}),runyiRoute:activeRoute,runyiStackDepth:routeStackRef.current.length};
    if (wasBackTransition) window.history.replaceState(state,"",window.location.href);
    else window.history.pushState(state,"",window.location.href);
  },[activeRoute]);

  function performBack(source: "TOP_BACK" | "ANDROID_SYSTEM_BACK", browserRoute?: unknown): boolean {
    // Visible menus are the newest functions; close them before the workspace.
    if (searchOpen || menuOpen) {
      setSearchOpen(false);
      setMenuOpen(false);
      return true;
    }

    // Advanced Factory is the previous function of New Infrastructure, not
    // a new primary tab. Restore the acquisition phone workspace first.
    if (activeRoute === "new" && !creationPlaneOpen) {
      setCreationPlaneOpen(true);
      return true;
    }

    const requested = new CustomEvent("runyi:request-back", {
      cancelable: true,
      detail: { route: activeRoute, source },
    });
    window.dispatchEvent(requested);
    if (requested.defaultPrevented) return true;

    const stack = routeStackRef.current;
    let previous = stack.pop();
    while (previous === activeRoute && stack.length > 0) previous = stack.pop();
    if (previous === activeRoute) previous = undefined;
    if (!previous && typeof browserRoute === "string"
      && allRoutes.includes(browserRoute as EnterpriseRoute) && browserRoute !== activeRoute) {
      previous = browserRoute as EnterpriseRoute;
    }
    if (!previous) return false;
    routeStackRef.current = stack;
    try { window.sessionStorage.setItem("runyi:route-stack", JSON.stringify(stack)); } catch {}
    setCanGoBack(stack.length > 0);
    backTransitionRef.current = true;
    restoreScrollRouteRef.current = previous;
    setSearchOpen(false);
    setMenuOpen(false);
    setQuery("");
    setUniversalHits([]);
    onNavigate(previous);
    return true;
  }

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onNativeBack = (event: Event) => {
      if (performBack("ANDROID_SYSTEM_BACK")) event.preventDefault();
    };
    const onPop = (event: PopStateEvent) => {
      const wasInWorkspace = activeRoute;
      const consumed = performBack("ANDROID_SYSTEM_BACK", event.state?.runyiRoute);
      // The browser has already moved an entry. Re-arm the current RUNYI
      // state only if a nested function was dismissed without a route change.
      if (consumed && previousRouteRef.current === wasInWorkspace
        && backTransitionRef.current === false) {
        window.history.pushState({
          ...(window.history.state ?? {}),
          runyiRoute: wasInWorkspace,
          runyiStackDepth: routeStackRef.current.length,
        }, "", window.location.href);
      }
    };
    window.addEventListener("runyi:native-back", onNativeBack);
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("runyi:native-back", onNativeBack);
      window.removeEventListener("popstate", onPop);
    };
  // The event handler must see the current route, menus and current callback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoute, onNavigate, searchOpen, menuOpen, creationPlaneOpen]);

  useEffect(() => { if (activeRoute === "new") setCreationPlaneOpen(true); },[activeRoute]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = `runyi:route-scroll:${activeRoute}`;
    if (restoreScrollRouteRef.current === activeRoute) {
      restoreScrollRouteRef.current = null;
      const saved = Number(window.sessionStorage.getItem(key) ?? "0");
      window.setTimeout(() => window.scrollTo({top:Number.isFinite(saved) ? saved : 0,behavior:"auto"}),0);
    }
    const save = () => {
      try { window.sessionStorage.setItem(key,String(window.scrollY)); } catch {}
    };
    window.addEventListener("scroll",save,{passive:true});
    return () => { save(); window.removeEventListener("scroll",save); };
  },[activeRoute]);

  function navigate(route: EnterpriseRoute) {
    setSearchOpen(false); setMenuOpen(false); setQuery(""); setUniversalHits([]); onNavigate(route);
  }

  function navigateUniversalHit(hit: UniversalSearchHit) {
    try { window.sessionStorage.setItem("runyi:universal-search-target",JSON.stringify(hit)); } catch {}
    navigate(hit.route);
  }
  function goBack() {
    if (typeof window === "undefined") return;
    void performBack("TOP_BACK");
  }
  const resultMaxHeight = Math.max(120,Math.min(420,(viewportHeight ?? 700)-250));

  return (
    <>
      <header ref={headerRef} data-runyi-ui-chrome="header" className="sticky top-0 z-50 border-b border-foreground/8 bg-[var(--top-bar)]/98 backdrop-blur-xl">
        <div className="mx-auto flex h-[62px] max-w-7xl items-center gap-2 px-3 sm:px-6">
          <button type="button" onClick={goBack} aria-label="Back" className="grid size-9 shrink-0 place-items-center rounded-xl border border-foreground/8 bg-foreground/[0.025] text-foreground/65 hover:bg-foreground/[0.06]"><ArrowLeft className="size-4.5" /></button>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5">
              <p className="min-w-0 truncate text-[9px] font-bold uppercase tracking-[0.2em] text-foreground/45">RUNYI ENTERPRISE</p>
              <span id="runyi-architecture-health-slot" className="flex shrink-0 items-center" />
              <span title={syncTitle} role="status" aria-label={syncTitle} className="flex max-w-20 shrink-0 items-center">
                <StatusIndicator status={globalValueSync.state} compact label={globalValueSync.state === "CURRENT" ? "CURRENT" : globalValueSync.state === "SYNCING" ? "SYNC" : globalValueSync.state === "ERROR" ? "ERROR" : "STALE"} className="max-w-20 px-1.5 text-[7px]" />
              </span>
            </div>
            <p className="truncate text-lg font-semibold leading-5">{routeTitles[activeRoute]}</p>
          </div>
          <div className="relative flex items-center gap-1">
            <button type="button" aria-label="Open RUNYI AI" onClick={() => navigate("ai")} className={`grid size-9 place-items-center rounded-xl border ${activeRoute === "ai" ? "border-primary/30 bg-primary/10 text-primary" : "border-foreground/8 bg-foreground/[0.025] text-foreground/65"}`}><Sparkles className="size-4.5" /></button>
            <button type="button" aria-label="Search RUNYI" onClick={() => { setSearchOpen((value) => !value); setMenuOpen(false); }} className="grid size-9 place-items-center rounded-xl border border-foreground/8 bg-foreground/[0.025] text-foreground/65">{searchOpen ? <X className="size-4.5" /> : <Search className="size-4.5" />}</button>
            <EnterpriseContextMenu label="RUNYI enterprise actions" title="Enterprise actions" width={240}
              controlledOpen={menuOpen} onOpenChange={(opened) => { setMenuOpen(opened); if (opened) setSearchOpen(false); }}>
              <MenuButton icon={HardDrive} label="Files" onClick={() => navigate("files")} />
              <MenuButton icon={Cable} label="Provider connections" onClick={() => navigate("bridge")} />
              <MenuButton icon={Settings2} label="Settings" onClick={() => navigate("governance")} />
              <MenuButton icon={RefreshCw} label={loading ? "Refreshing…" : "Synchronize enterprise values"} onClick={() => { setMenuOpen(false); refreshUniversalValues(); }} />
              <p role="status" className="break-words border-t border-foreground/10 px-2.5 py-2 text-[10px] leading-4 text-foreground/55">{syncTitle}</p>
            </EnterpriseContextMenu>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-foreground/8 px-3 pb-3 pt-2 sm:px-6">
            <div className="mx-auto max-w-3xl">
              <div className="mb-1 flex items-center justify-between px-1">
                <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-primary">Universal RUNYI Search</p>
                <p className="text-[9px] text-foreground/35">Authorized system scope</p>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground/35" />
                <Input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search names, emails, infrastructure, applications, projects, services and functions…" className="h-11 rounded-xl border-foreground/10 bg-background pl-9 pr-3 text-[16px] shadow-sm" />
              </div>
              <div className="mt-2 overflow-y-auto overscroll-contain rounded-2xl border border-foreground/10 bg-popover p-1.5 shadow-xl" style={{ maxHeight: resultMaxHeight }}>
                {!query.trim() ? (
                  <div className="px-3 py-3"><p className="text-xs font-semibold">Search the authorized RUNYI system</p><p className="mt-1 text-[11px] leading-5 text-foreground/40">Header search is universal. Each workspace keeps its own local search.</p><div className="mt-2 flex flex-wrap gap-1.5"><QuickSearch label="Supabase" onClick={() => setQuery("Supabase")} /><QuickSearch label="Applications" onClick={() => setQuery("application")} /><QuickSearch label="Projects" onClick={() => setQuery("project")} /><QuickSearch label="Settings" onClick={() => setQuery("settings")} /></div></div>
                ) : universalHits.length === 0 && globalResults.length === 0 ? (
                  <SearchEmpty text="No authorized RUNYI object or function matches this search." />
                ) : (
                  <>
                    {universalHits.length > 0 && <div className="py-1"><p className="px-3 pb-1 pt-2 text-[9px] font-bold uppercase tracking-[0.15em] text-foreground/30">System results</p>{universalHits.map((hit) => <button key={hit.key} type="button" onClick={() => navigateUniversalHit(hit)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-foreground/[0.06]"><span className="grid size-8 shrink-0 place-items-center rounded-lg bg-foreground/[0.04]"><Search className="size-4" /></span><span className="min-w-0 flex-1"><span className="block truncate text-xs font-semibold">{hit.title}</span><span className="mt-0.5 block truncate text-[10px] text-foreground/35">{hit.subtitle}</span></span><ChevronRight className="size-4 shrink-0 text-foreground/25" /></button>)}</div>}
                    {Object.entries(groupedResults).map(([group,items]) => <div key={group} className="py-1"><p className="px-3 pb-1 pt-2 text-[9px] font-bold uppercase tracking-[0.15em] text-foreground/30">{group}</p>{items.map((item,index) => <button key={`${group}-${item.id}-${item.label}-${index}`} type="button" onClick={() => navigate(item.id)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-foreground/[0.06]"><span className="grid size-8 shrink-0 place-items-center rounded-lg bg-foreground/[0.04]"><item.icon className="size-4" /></span><span className="min-w-0 flex-1"><span className="block text-xs font-semibold">{item.label}</span><span className="mt-0.5 block truncate text-[10px] text-foreground/35">{item.description}</span></span><ChevronRight className="size-4 shrink-0 text-foreground/25" /></button>)}</div>)}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {activeRoute === "new" && creationPlaneOpen && typeof document !== "undefined" && createPortal(
        <div id="runyi-uicp" className="runyi-enterprise-screen fixed inset-x-0 bottom-[calc(56px+env(safe-area-inset-bottom))] top-[62px] z-[45] overflow-y-auto overscroll-contain bg-background lg:bottom-0">
          <UniversalInfrastructureCreationPlane
            onNavigate={onNavigate}
            onOpenAdvancedFactory={() => setCreationPlaneOpen(false)}
            supabase={supabase}
            connected={health.connected}
            role={health.role}
          />
        </div>,
        document.body,
      )}
    </>
  );
}

export function EnterpriseSidebar({
  activeRoute,
  onNavigate,
  supabase = null,
  connected = false,
}: {
  activeRoute: EnterpriseRoute;
  onNavigate: (route: EnterpriseRoute) => void;
  supabase?: SupabaseClient | null;
  connected?: boolean;
}) {
  const primary = primaryRouteFor(activeRoute);
  const controlPlane = useRunyiUiControlPlane({ supabase, connected, screenKey: "ROOT" });
  const navigation = resolveControlPlanePrimaryNavigation(controlPlane);
  return <aside className="hidden lg:block"><div className="sticky top-[82px] space-y-1 rounded-2xl border border-foreground/8 bg-card p-2">{navigation.map((item) => <button key={item.id} type="button" onClick={() => onNavigate(item.id)} className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition ${primary === item.id ? "bg-foreground/10 font-semibold text-foreground" : "text-foreground/50 hover:bg-foreground/[0.05] hover:text-foreground"}`}><item.icon className="size-4" /><span>{item.label}</span></button>)}<div className="my-2 border-t border-foreground/8" /><button type="button" onClick={() => onNavigate("ai")} className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm ${activeRoute === "ai" ? "bg-primary/10 font-semibold text-primary" : "text-foreground/50 hover:bg-foreground/[0.05]"}`}><Sparkles className="size-4" />AI Assistant</button></div></aside>;
}

export function EnterpriseBottomNav({
  activeRoute,
  onNavigate,
  supabase = null,
  connected = false,
}: {
  activeRoute: EnterpriseRoute;
  onNavigate: (route: EnterpriseRoute) => void;
  supabase?: SupabaseClient | null;
  connected?: boolean;
}) {
  const primary = primaryRouteFor(activeRoute);
  const controlPlane = useRunyiUiControlPlane({ supabase, connected, screenKey: "ROOT" });
  const navigation = resolveControlPlanePrimaryNavigation(controlPlane);
  return <nav aria-label="RUNYI primary workspaces" data-runyi-ui-chrome="bottom-nav" className="fixed inset-x-0 bottom-0 z-50 border-t border-foreground/8 bg-[var(--top-bar)]/98 px-1 pb-[max(4px,env(safe-area-inset-bottom))] pt-1 backdrop-blur-xl lg:hidden"><div className="mx-auto grid max-w-xl" style={{ gridTemplateColumns: "repeat(" + Math.max(1, navigation.length) + ", minmax(0, 1fr))" }}>{navigation.map((item) => <button key={item.id} type="button" onClick={() => onNavigate(item.id)} aria-current={primary === item.id ? "page" : undefined} aria-label={item.label} className={`flex min-w-0 flex-col items-center gap-0.5 rounded-xl px-1 py-1.5 text-[10px] transition ${primary === item.id ? "bg-foreground/10 font-semibold text-foreground" : "text-foreground/42"}`}><item.icon className="size-4" /><span className="truncate">{item.mobileLabel}</span></button>)}</div></nav>;
}

export function EnterpriseHome({ health, onNavigate }: { health: HealthLike; onNavigate: (route: EnterpriseRoute) => void }) {
  const cards: Array<{ title:string; detail:string; route:EnterpriseRoute; icon:LucideIcon; badge:string }> = [
    { title:"Infrastructure", detail:"Inventory, provider truth, usage and capacity.", route:"infrastructure", icon:Database, badge:health.connected ? "CONNECTED" : "CHECK" },
    { title:"Universal Create", detail:"Create or register provider-neutral resources.", route:"new", icon:Plus, badge:"ACTIVE" },
    { title:"Applications", detail:"Application workspaces and assigned infrastructure.", route:"applications", icon:AppWindow, badge:"ACTIVE" },
    { title:"Operations", detail:"Provisioning, deployment, reconciliation and jobs.", route:"operations", icon:Workflow, badge:"ACTIVE" },
    { title:"Files", detail:"Universal Workspace Explorer across governed provider mounts.", route:"files", icon:HardDrive, badge:"UWE V2" },
    { title:"RUNYI AI", detail:"Permission-aware system assistant.", route:"ai", icon:Sparkles, badge:"ACTIVE" },
  ];
  return <div className="space-y-5"><section><p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Unlimited Universal Infrastructure Marketplace</p><h1 className="mt-1 text-3xl font-semibold tracking-[-0.035em]">RUNYI Enterprise</h1><p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-foreground/42">Enterprise Control Tower</p><p className="mt-2 max-w-2xl text-sm leading-6 text-foreground/48">Discover, connect, create, verify, compose and operate provider-neutral infrastructure for applications, services and governed automation.</p></section><div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{cards.map((card) => <button key={card.title} type="button" onClick={() => onNavigate(card.route)} className="rounded-[22px] border border-foreground/8 bg-card p-4 text-left shadow-sm transition hover:border-foreground/15 hover:bg-foreground/[0.025]"><div className="flex items-start justify-between gap-3"><span className="grid size-10 place-items-center rounded-xl border border-foreground/8 bg-foreground/[0.025]"><card.icon className="size-5" /></span><Badge variant="outline" className="border-foreground/10 text-[8px] text-foreground/45">{card.badge}</Badge></div><p className="mt-4 text-base font-semibold">{card.title}</p><p className="mt-1 text-xs leading-5 text-foreground/42">{card.detail}</p></button>)}</div><div className="grid gap-3 sm:grid-cols-3"><Metric label="Role" value={health.role ?? "—"} /><Metric label="Accounts" value={`${health.activeUserCount ?? 0}${health.maximumUsers ? ` / ${health.maximumUsers}` : ""}`} /><Metric label="Control plane" value={health.connected ? "Online" : "Check"} status /></div></div>;
}

export function EnterpriseMoreWorkspace({ onNavigate }: { onNavigate: (route: EnterpriseRoute) => void }) {
  const [query,setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const groups = functionalGroups.filter((group) => ["AI","Data & Files","Governance & Tools"].includes(group.title));
  return <div className="space-y-5"><section><p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Enterprise directory</p><h1 className="mt-1 text-2xl font-semibold tracking-[-0.025em] sm:text-3xl">More</h1><p className="mt-1 max-w-2xl text-sm leading-6 text-foreground/48">AI, universal files, governance and secondary tools.</p></section><div className="relative z-10 w-full min-w-0 rounded-2xl border border-foreground/8 bg-[var(--top-bar)]/95 p-1.5"><div className="relative"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground/35" /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search AI, files, governance, settings and tools…" className="h-10 rounded-xl border-foreground/10 bg-background pl-9 text-[16px]" /></div></div><div className="space-y-6">{groups.map((group) => { const items = group.items.filter((item) => !needle || [item.label,item.description,...(item.keywords ?? [])].filter(Boolean).join(" ").toLowerCase().includes(needle)); if (!items.length) return null; return <section key={group.title}><p className="px-1 text-sm font-semibold">{group.title}</p><p className="mt-0.5 px-1 text-xs text-foreground/38">{group.description}</p><div className="mt-2 space-y-2">{items.map((item,index) => <button key={`${item.id}-${item.label}-${index}`} type="button" onClick={() => onNavigate(item.id)} className="flex w-full items-center gap-3 rounded-[22px] border border-foreground/8 bg-card p-3.5 text-left transition hover:border-foreground/15 hover:bg-foreground/[0.025]"><span className="grid size-11 shrink-0 place-items-center rounded-xl border border-foreground/8 bg-foreground/[0.025]"><item.icon className="size-5" /></span><span className="min-w-0 flex-1"><span className="block text-sm font-semibold sm:text-base">{item.label}</span><span className="mt-0.5 block text-xs leading-5 text-foreground/40">{item.description}</span></span><ChevronRight className="size-4 shrink-0 text-foreground/25" /></button>)}</div></section>; })}</div></div>;
}

function Metric({ label,value,status = false }: { label:string; value:string; status?:boolean }) { return <Card className="border-foreground/8 bg-card py-0"><CardContent className="p-3"><p className="text-[9px] font-bold uppercase tracking-[0.14em] text-foreground/30">{label}</p>{status ? <Badge status={value.toUpperCase()} className="mt-1">{value}</Badge> : <p className="mt-1 truncate text-sm font-semibold">{value}</p>}</CardContent></Card>; }
function SearchEmpty({ text }: { text:string }) { return <div className="px-3 py-5 text-center text-xs leading-5 text-foreground/40">{text}</div>; }
function QuickSearch({ label,onClick }: { label:string; onClick:()=>void }) { return <button type="button" onClick={onClick} className="rounded-lg border border-foreground/8 bg-foreground/[0.025] px-2.5 py-1.5 text-[10px] text-foreground/55 hover:bg-foreground/[0.06]">{label}</button>; }
function MenuButton({ icon:Icon,label,onClick }: { icon:LucideIcon; label:string; onClick:()=>void }) { return <button type="button" onClick={onClick} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-xs hover:bg-foreground/[0.06]"><Icon className="size-4" />{label}</button>; }

export function EnterpriseSection({ children }: { children:ReactNode }) { return <>{children}</>; }
